import BalanceHistory from '../components/BalanceHistory'
import { loadBalanceSnapshots } from '../lib/loadBalanceSnapshots'
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Info, ChevronLeft, ChevronRight, LineChart as LineChartIcon } from 'lucide-react'
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, LineChart, Line, Legend,
  ScatterChart, Scatter, ZAxis, ReferenceLine,
} from 'recharts'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { usePrivacy } from '../hooks/usePrivacy'
import { formatOCC } from '../lib/occParser'
import PrivateValue from '../components/PrivateValue'
import InfoTooltip from '../components/InfoTooltip'
import MetricTrendSlideOver from '../components/MetricTrendSlideOver'

// ── Metrics History Capture ──────────────────────────────────────────────────

const DEMO_EMAIL = 'demo@erevis.app'

function captureMetricsToHistory(user, metricView, metricsMap) {
  if (!user?.id || user.email === DEMO_EMAIL) return
  if (!metricsMap || Object.keys(metricsMap).length === 0) return

  const today = new Date()
  const metricDate = today.getFullYear() + '-' +
    String(today.getMonth() + 1).padStart(2, '0') + '-' +
    String(today.getDate()).padStart(2, '0')

  const rows = []
  for (const [key, val] of Object.entries(metricsMap)) {
    if (val == null || !isFinite(val)) continue
    const isBenchmarkMetric = ['beta', 'alpha', 'r_squared', 'correlation', 'tracking_error', 'info_ratio'].includes(key)
    rows.push({
      user_id: user.id,
      metric_date: metricDate,
      metric_name: key,
      metric_value: val,
      metric_view: metricView,
      benchmark: isBenchmarkMetric ? 'SPY' : '',
    })
  }

  if (rows.length === 0) return

  // Fire and forget
  supabase
    .from('metrics_history')
    .upsert(rows, { onConflict: 'user_id,metric_date,metric_name,metric_view,benchmark' })
    .then(({ error }) => {
      if (error) console.warn('metrics_history upsert error:', error.message)
    })
}

// ── Chart Color Palette ───────────────────────────────────────────────────────
const CHART_COLORS = [
  '#4a6741', '#6aaa5e', '#5b7ea1', '#c4953a', '#7c6bb5',
  '#4a9c8c', '#d4764e', '#b83a2e', '#e05a4a', '#8a92a0',
]

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtCurrency(val) {
  if (val == null) return '—'
  return Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtPct(val) {
  if (val == null) return '—'
  const n = Number(val)
  return (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
}

function fmtSigned(val) {
  if (val == null) return '—'
  const n = Number(val)
  return (n >= 0 ? '+' : '') + fmtCurrency(n)
}

function clr(val) {
  if (val > 0) return 'text-forest'
  if (val < 0) return 'text-ember'
  return 'text-inkMuted'
}

function fmtSymbol(symbol, assetType) {
  if (assetType === 'option') return formatOCC(symbol)
  return symbol || '—'
}

// ── Filter Pill ───────────────────────────────────────────────────────────────

function Pill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`font-mono text-[10px] px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
        active
          ? 'bg-forest text-white border-forest'
          : 'bg-canvas2 text-inkMuted border-border hover:text-ink'
      }`}
    >
      {label}
    </button>
  )
}

// ── Date Range Presets ────────────────────────────────────────────────────────

function fmtISO(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

function getDateRange(preset) {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const d = now.getDate()
  const today = new Date(y, m, d)

  switch (preset) {
    case 'today':
      return [fmtISO(today), fmtISO(today)]
    case 'thisWeek': {
      const day = today.getDay()
      const mon = new Date(y, m, d - (day === 0 ? 6 : day - 1))
      return [fmtISO(mon), fmtISO(today)]
    }
    case 'lastWeek': {
      const day = today.getDay()
      const thisMon = new Date(y, m, d - (day === 0 ? 6 : day - 1))
      const lastMon = new Date(thisMon.getFullYear(), thisMon.getMonth(), thisMon.getDate() - 7)
      const lastSun = new Date(thisMon.getFullYear(), thisMon.getMonth(), thisMon.getDate() - 1)
      return [fmtISO(lastMon), fmtISO(lastSun)]
    }
    case 'thisMonth':
      return [fmtISO(new Date(y, m, 1)), fmtISO(today)]
    case 'lastMonth':
      return [fmtISO(new Date(y, m - 1, 1)), fmtISO(new Date(y, m, 0))]
    case 'thisQuarter': {
      const qStart = new Date(y, Math.floor(m / 3) * 3, 1)
      return [fmtISO(qStart), fmtISO(today)]
    }
    case 'lastQuarter': {
      const cqStart = Math.floor(m / 3) * 3
      return [fmtISO(new Date(y, cqStart - 3, 1)), fmtISO(new Date(y, cqStart, 0))]
    }
    case 'thisYear':
      return [fmtISO(new Date(y, 0, 1)), fmtISO(today)]
    case 'lastYear':
      return [fmtISO(new Date(y - 1, 0, 1)), fmtISO(new Date(y - 1, 11, 31))]
    case 'last30': {
      const start = new Date(y, m, d - 30)
      return [fmtISO(start), fmtISO(today)]
    }
    case 'last90': {
      const start = new Date(y, m, d - 90)
      return [fmtISO(start), fmtISO(today)]
    }
    default:
      return ['', '']
  }
}

const DATE_PRESETS = [
  { key: 'all', label: 'All Time' },
  { key: 'today', label: 'Today' },
  { key: 'thisWeek', label: 'This Week' },
  { key: 'lastWeek', label: 'Last Week' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'thisQuarter', label: 'This Qtr' },
  { key: 'lastQuarter', label: 'Last Qtr' },
  { key: 'thisYear', label: 'This Year' },
  { key: 'lastYear', label: 'Last Year' },
  { key: 'last30', label: 'Last 30d' },
  { key: 'last90', label: 'Last 90d' },
  { key: 'custom', label: 'Custom' },
]

// ── Stat Badge ────────────────────────────────────────────────────────────────

function StatBadge({ label, value, colorClass, onClick }) {
  return (
    <div
      className={`inline-flex items-center gap-1.5 bg-canvas2 border border-border rounded-lg px-2.5 py-1${onClick ? ' metric-card-clickable' : ''}`}
      onClick={onClick}
    >
      <span className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">{label}</span>
      <span className={`font-mono text-xs font-semibold ${colorClass || 'text-ink'}`}>{value}</span>
    </div>
  )
}

// ── Recharts Custom Tooltip ──────────────────────────────────────────────────

function ChartTooltip({ active, payload, label, isCurrency }) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-canvas border border-border rounded-lg px-3 py-2 shadow-lg">
      <div className="font-mono text-[10px] text-inkMuted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="font-mono text-xs font-semibold" style={{ color: p.color }}>
          {isCurrency ? fmtCurrency(p.value) : p.value?.toFixed(2) + '%'}
        </div>
      ))}
    </div>
  )
}

// ── Section 1: Performance KPI Cards ─────────────────────────────────────────

function PerformanceKPIs({ trades, totalEquity, onMetricClick, onMetricsComputed }) {
  const closed = trades.filter(t => t.status === 'closed' && t.net_pnl != null)
  const wins = closed.filter(t => Number(t.net_pnl) > 0)
  const losses = closed.filter(t => Number(t.net_pnl) < 0)

  const netPnl = closed.reduce((s, t) => s + Number(t.net_pnl || 0), 0)
  const totalWins = wins.reduce((s, t) => s + Number(t.net_pnl), 0)
  const totalLosses = losses.reduce((s, t) => s + Number(t.net_pnl), 0)
  const profitFactor = totalLosses !== 0 ? totalWins / Math.abs(totalLosses) : (totalWins > 0 ? Infinity : 0)
  const winRate = closed.length > 0 ? (wins.length / closed.length) * 100 : 0

  // Return %: net P&L / starting equity (current equity minus net P&L)
  const startingEquity = (totalEquity || 0) - netPnl
  const twReturn = startingEquity > 0 ? (netPnl / startingEquity) * 100 : 0

  // Avg R-Multiple
  const rTrades = closed.filter(t => t.r_multiple != null)
  const avgR = rTrades.length > 0 ? rTrades.reduce((s, t) => s + Number(t.r_multiple), 0) / rTrades.length : null

  // Report metrics for capture
  useEffect(() => {
    if (onMetricsComputed) {
      onMetricsComputed({
        return_pct: twReturn,
        win_rate: winRate,
        profit_factor: profitFactor === Infinity ? null : profitFactor,
        avg_r_multiple: avgR,
      })
    }
  }, [twReturn, winRate, profitFactor, avgR, onMetricsComputed])

  const mc = (name, value) => onMetricClick ? () => onMetricClick(name, value) : undefined

  return (
    <div>
      <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Performance</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="card">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Net P&L<InfoTooltip text="Total realized profit and loss from all closed trades in the selected period, after commissions and fees." /></div>
          <div className={`font-mono text-xl font-semibold ${clr(netPnl)}`}>
            <PrivateValue>{fmtSigned(netPnl)}</PrivateValue>
          </div>
        </div>
        <div className="card metric-card-clickable" onClick={mc('return_pct', twReturn)}>
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Return %<InfoTooltip text="Net P&L as a percentage of starting portfolio equity. Measures capital efficiency of closed trades only." /></div>
          <div className={`font-mono text-xl font-semibold ${clr(twReturn)}`}>
            <PrivateValue>{fmtPct(twReturn)}</PrivateValue>
          </div>
        </div>
        <div className="card metric-card-clickable" onClick={mc('win_rate', winRate)}>
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Win Rate<InfoTooltip text="Percentage of closed trades that were profitable. Most successful growth stock traders operate between 30-50% win rate with large average wins." /></div>
          <div className={`font-mono text-xl font-semibold ${winRate >= 50 ? 'text-forest' : 'text-ember'}`}>
            {winRate.toFixed(1)}%
          </div>
          <div className="font-mono text-[10px] text-inkMuted mt-1">{wins.length}W / {losses.length}L</div>
        </div>
        <div className="card metric-card-clickable" onClick={mc('profit_factor', profitFactor === Infinity ? null : profitFactor)}>
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Profit Factor<InfoTooltip text="Gross profits divided by gross losses. Above 1.0 means profitable overall. Above 2.0 is strong. Above 3.0 is exceptional." /></div>
          <div className={`font-mono text-xl font-semibold ${profitFactor >= 1 ? 'text-forest' : 'text-ember'}`}>
            <PrivateValue>{profitFactor === Infinity ? '∞' : profitFactor.toFixed(2)}</PrivateValue>
          </div>
        </div>
        <div className="card">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Total Trades<InfoTooltip text="Number of closed trades in the selected period." /></div>
          <div className="font-mono text-xl font-semibold text-ink">{closed.length}</div>
        </div>
        <div className="card metric-card-clickable" onClick={mc('avg_r_multiple', avgR)}>
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Avg R-Multiple<InfoTooltip text="Average return expressed as a multiple of initial risk taken. An R-multiple of 2.0 means the average trade returned 2x what was risked. Only includes trades with stop loss data." /></div>
          <div className={`font-mono text-xl font-semibold ${avgR != null ? clr(avgR) : 'text-inkMuted'}`}>
            {avgR != null ? avgR.toFixed(2) : '—'}
          </div>
          {rTrades.length > 0 && (
            <div className="font-mono text-[10px] text-inkMuted mt-1">{rTrades.length} trades w/ stop</div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Section 2: Equity Curve & Drawdown ───────────────────────────────────────

function EquityCurveSection({ trades }) {
  const closed = useMemo(() => {
    return [...trades]
      .filter(t => t.status === 'closed' && t.net_pnl != null && t.exit_date)
      .sort((a, b) => (a.exit_date + (a.exit_time || '')).localeCompare(b.exit_date + (b.exit_time || '')))
  }, [trades])

  // Cumulative P&L data
  const { curveData, drawdownData, maxDD, maxDDPct, currentDD, currentDDPct } = useMemo(() => {
    let cumPnl = 0
    let peak = 0
    let maxDrawdown = 0
    let maxDrawdownPct = 0
    let currentDrawdown = 0
    let currentDrawdownPct = 0

    const curve = []
    const dd = []

    for (const t of closed) {
      cumPnl += Number(t.net_pnl)
      if (cumPnl > peak) peak = cumPnl
      const drawdown = peak - cumPnl
      const drawdownPct = peak > 0 ? (drawdown / peak) * 100 : 0
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown
        maxDrawdownPct = drawdownPct
      }
      currentDrawdown = drawdown
      currentDrawdownPct = drawdownPct

      const label = t.exit_date
      curve.push({ date: label, value: cumPnl })
      dd.push({ date: label, value: -drawdownPct })
    }

    return {
      curveData: curve,
      drawdownData: dd,
      maxDD: maxDrawdown,
      maxDDPct: maxDrawdownPct,
      currentDD: currentDrawdown,
      currentDDPct: currentDrawdownPct,
    }
  }, [closed])

  // Period return badges
  const periodReturns = useMemo(() => {
    if (closed.length === 0) return []
    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth()
    const d = now.getDate()
    const allTimePnl = closed.reduce((s, t) => s + Number(t.net_pnl), 0)

    const cutoffs = [
      { label: '1M', date: fmtISO(new Date(y, m - 1, d)) },
      { label: '3M', date: fmtISO(new Date(y, m - 3, d)) },
      { label: 'YTD', date: fmtISO(new Date(y, 0, 1)) },
    ]

    const badges = cutoffs.map(({ label, date }) => {
      const sum = closed.filter(t => t.exit_date >= date).reduce((s, t) => s + Number(t.net_pnl), 0)
      return { label, value: fmtSigned(sum), colorClass: clr(sum) }
    })

    badges.push({ label: 'All', value: fmtSigned(allTimePnl), colorClass: clr(allTimePnl) })
    return badges
  }, [closed])

  if (closed.length === 0) {
    return (
      <div>
        <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Equity Curve & Drawdown</h2>
        <div className="card text-center py-12">
          <div className="font-mono text-xs text-inkMuted">No closed trades to chart</div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Equity Curve & Drawdown</h2>

      {/* Cumulative P&L */}
      <div className="card mb-4">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="font-mono text-xs font-semibold text-ink mr-2">Cumulative P&L</span>
          {periodReturns.map(b => (
            <PrivateValue key={b.label}>
              <StatBadge label={b.label} value={b.value} colorClass={b.colorClass} />
            </PrivateValue>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={curveData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="pnlGradientPos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(var(--color-forest))" stopOpacity={0.3} />
                <stop offset="100%" stopColor="rgb(var(--color-forest))" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="pnlGradientNeg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(var(--color-ember))" stopOpacity={0.02} />
                <stop offset="100%" stopColor="rgb(var(--color-ember))" stopOpacity={0.3} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
              axisLine={{ stroke: 'rgb(var(--color-border))' }}
              tickLine={false}
              minTickGap={60}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={v => v >= 1000 || v <= -1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
              width={55}
            />
            <Tooltip content={<ChartTooltip isCurrency />} />
            <Area
              type="monotone"
              dataKey="value"
              stroke={curveData[curveData.length - 1]?.value >= 0 ? 'rgb(var(--color-forest))' : 'rgb(var(--color-ember))'}
              strokeWidth={2}
              fill={curveData[curveData.length - 1]?.value >= 0 ? 'url(#pnlGradientPos)' : 'url(#pnlGradientNeg)'}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Drawdown */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="font-mono text-xs font-semibold text-ink mr-2">Drawdown</span>
          <StatBadge label="Max DD" value={`-${maxDDPct.toFixed(1)}%`} colorClass="text-ember" />
          <PrivateValue>
            <StatBadge label="Max DD $" value={fmtCurrency(maxDD)} colorClass="text-ember" />
          </PrivateValue>
          <StatBadge label="Current" value={currentDDPct > 0 ? `-${currentDDPct.toFixed(1)}%` : '0.00%'} colorClass={currentDDPct > 0 ? 'text-ember' : 'text-forest'} />
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={drawdownData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="ddGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(var(--color-ember))" stopOpacity={0.05} />
                <stop offset="100%" stopColor="rgb(var(--color-ember))" stopOpacity={0.4} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
              axisLine={{ stroke: 'rgb(var(--color-border))' }}
              tickLine={false}
              minTickGap={60}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={v => `${v.toFixed(0)}%`}
              width={45}
              domain={['dataMin', 0]}
            />
            <Tooltip content={<ChartTooltip />} />
            <Area
              type="monotone"
              dataKey="value"
              stroke="rgb(var(--color-ember))"
              strokeWidth={1.5}
              fill="url(#ddGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// ── Section 3: Risk-Adjusted Metrics ─────────────────────────────────────────

function RiskMetrics({ trades, onMetricClick, onMetricsComputed }) {
  const metrics = useMemo(() => {
    const closed = trades.filter(t => t.status === 'closed' && t.net_pnl != null && t.exit_date)
    if (closed.length < 2) return null

    // Group by exit_date to get daily P&L
    const dailyMap = {}
    for (const t of closed) {
      const d = t.exit_date
      dailyMap[d] = (dailyMap[d] || 0) + Number(t.net_pnl)
    }
    const dailyPnls = Object.values(dailyMap)
    const n = dailyPnls.length
    if (n < 2) return null

    const mean = dailyPnls.reduce((s, v) => s + v, 0) / n
    const variance = dailyPnls.reduce((s, v) => s + (v - mean) ** 2, 0) / (n - 1)
    const stdDev = Math.sqrt(variance)

    // Downside deviation (only negative returns)
    const negPnls = dailyPnls.filter(v => v < 0)
    const downsideVariance = negPnls.length > 0
      ? negPnls.reduce((s, v) => s + v ** 2, 0) / negPnls.length
      : 0
    const downsideDev = Math.sqrt(downsideVariance)

    // Risk-free rate per day
    const rfDaily = 0.05 / 252

    // Sharpe Ratio (annualized)
    const sharpe = stdDev > 0 ? ((mean - rfDaily) / stdDev) * Math.sqrt(252) : 0

    // Sortino Ratio (annualized)
    const sortino = downsideDev > 0 ? ((mean - rfDaily) / downsideDev) * Math.sqrt(252) : 0

    // Cumulative P&L for drawdown
    const sorted = [...closed].sort((a, b) => (a.exit_date + (a.exit_time || '')).localeCompare(b.exit_date + (b.exit_time || '')))
    let cumPnl = 0, peak = 0, maxDDDollar = 0, maxDDPct = 0
    for (const t of sorted) {
      cumPnl += Number(t.net_pnl)
      if (cumPnl > peak) peak = cumPnl
      const dd = peak - cumPnl
      const ddPct = peak > 0 ? (dd / peak) * 100 : 0
      if (dd > maxDDDollar) { maxDDDollar = dd; maxDDPct = ddPct }
    }

    const netPnl = sorted.reduce((s, t) => s + Number(t.net_pnl), 0)

    // Annualized return for Calmar
    const firstDate = new Date(sorted[0].exit_date)
    const lastDate = new Date(sorted[sorted.length - 1].exit_date)
    const daySpan = Math.max((lastDate - firstDate) / (1000 * 60 * 60 * 24), 1)
    const annualizedReturn = (netPnl / (daySpan / 365))

    // Calmar Ratio
    const calmar = maxDDDollar > 0 ? annualizedReturn / maxDDDollar : 0

    // Recovery Factor
    const recoveryFactor = maxDDDollar > 0 ? netPnl / maxDDDollar : 0

    // Consistency Score: MAX(0, (1 - stdDev / (|mean| + 0.01)) * 100), clamped 0-100
    // Requires at least 5 trading days for a meaningful score
    const consistencyNA = n < 5
    const rawConsistency = !consistencyNA ? (1 - (stdDev / (Math.abs(mean) + 0.01))) * 100 : 0
    const consistency = consistencyNA ? null : Math.max(0, Math.min(100, Math.round(rawConsistency)))

    return {
      sharpe,
      sortino,
      calmar,
      maxDDDollar,
      maxDDPct,
      recoveryFactor,
      consistency,
      consistencyNA,
    }
  }, [trades])

  // Report metrics for capture
  useEffect(() => {
    if (onMetricsComputed && metrics) {
      onMetricsComputed({
        sharpe_ratio: metrics.sharpe,
        sortino_ratio: metrics.sortino,
        calmar_ratio: metrics.calmar,
        max_drawdown_pct: metrics.maxDDPct,
        recovery_factor: metrics.recoveryFactor,
        consistency_score: metrics.consistency,
      })
    }
  }, [metrics, onMetricsComputed])

  if (!metrics) {
    return (
      <div>
        <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Risk-Adjusted Metrics</h2>
        <div className="card text-center py-8">
          <div className="font-mono text-xs text-inkMuted">Need at least 2 trading days for risk metrics</div>
        </div>
      </div>
    )
  }

  const mc = (name, value) => onMetricClick ? () => onMetricClick(name, value) : undefined

  const cards = [
    { label: 'Sharpe Ratio', metricName: 'sharpe_ratio', rawValue: metrics.sharpe, value: metrics.sharpe.toFixed(2), desc: 'Return per unit of total risk', colorClass: clr(metrics.sharpe), tip: 'Risk-adjusted return based on daily realized P&L volatility. Measures consistency of trading profits.' },
    { label: 'Sortino Ratio', metricName: 'sortino_ratio', rawValue: metrics.sortino, value: metrics.sortino.toFixed(2), desc: 'Return per unit of downside risk', colorClass: clr(metrics.sortino), tip: 'Like Sharpe but only penalizes losing days, not winning days. Better for strategies with occasional large wins.' },
    { label: 'Calmar Ratio', metricName: 'calmar_ratio', rawValue: metrics.calmar, value: metrics.calmar.toFixed(2), desc: 'Annualized return vs max drawdown', colorClass: clr(metrics.calmar), tip: 'Annualized return divided by max drawdown. Measures return relative to worst-case scenario.' },
    {
      label: 'Max Drawdown',
      metricName: 'max_drawdown_pct',
      rawValue: metrics.maxDDPct,
      value: `-${metrics.maxDDPct.toFixed(1)}%`,
      subValue: fmtCurrency(metrics.maxDDDollar),
      desc: 'Largest peak-to-trough decline',
      colorClass: 'text-ember',
      isPrivate: true,
      tip: 'Largest cumulative P&L decline from peak to trough during the period.',
    },
    { label: 'Recovery Factor', metricName: 'recovery_factor', rawValue: metrics.recoveryFactor, value: metrics.recoveryFactor.toFixed(2), desc: 'Net P&L relative to max drawdown', colorClass: clr(metrics.recoveryFactor), tip: 'Net P&L divided by max drawdown. How many times over the strategy has recovered its worst loss.' },
    { label: 'Consistency', metricName: 'consistency_score', rawValue: metrics.consistency, value: metrics.consistencyNA ? 'N/A' : metrics.consistency.toFixed(0), desc: metrics.consistencyNA ? 'Need 5+ trading days' : 'P&L stability score (0-100)', colorClass: metrics.consistencyNA ? 'text-inkMuted' : (metrics.consistency >= 50 ? 'text-forest' : 'text-ember'), tip: 'Score from 0-100 measuring how consistent daily P&L is. Higher means more predictable returns.' },
  ]

  return (
    <div>
      <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Risk-Adjusted Metrics</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map(c => (
          <div key={c.label} className="card metric-card-clickable" onClick={mc(c.metricName, c.rawValue)}>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">{c.label}{c.tip && <InfoTooltip text={c.tip} />}</div>
            <div className={`font-mono text-xl font-semibold ${c.colorClass}`}>
              {c.isPrivate ? <PrivateValue>{c.value}</PrivateValue> : c.value}
            </div>
            {c.subValue && (
              <div className="font-mono text-xs text-ember mt-0.5">
                <PrivateValue>{c.subValue}</PrivateValue>
              </div>
            )}
            <div className="font-mono text-[9px] text-inkMuted mt-2 leading-tight">{c.desc}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Section 4: Portfolio at Risk ─────────────────────────────────────────────

function PortfolioAtRisk({ openTrades, positions, accounts, nicknameMap }) {
  const { totalEquity, riskRows, totalRisk, totalRiskPct, largestRisk } = useMemo(() => {
    // Total portfolio equity from schwab_accounts (equity is JSONB: {equity: number})
    const equity = accounts.reduce((s, a) => s + Number(a.total_value || a.equity?.equity || 0), 0)

    // Build position map for quantity_open lookup
    const posMap = {}
    for (const p of positions) {
      const key = `${p.account_number}_${p.symbol}`
      posMap[key] = p
    }

    const rows = []
    for (const t of openTrades) {
      if (!t.stop_loss) continue
      const sharesAtRisk = Number(t.quantity_open || 0)
      if (sharesAtRisk <= 0) continue

      const entry = Number(t.avg_entry_price)
      const stop = Number(t.stop_loss)
      const dollarRisk = t.side === 'long'
        ? (entry - stop) * sharesAtRisk
        : (stop - entry) * sharesAtRisk
      const pctOfPortfolio = equity > 0 ? (Math.abs(dollarRisk) / equity) * 100 : 0

      rows.push({
        id: t.id,
        symbol: t.symbol,
        account: t.account_number,
        side: t.side,
        entry,
        stop,
        shares: sharesAtRisk,
        dollarRisk: Math.abs(dollarRisk),
        pctOfPortfolio,
      })
    }

    rows.sort((a, b) => b.dollarRisk - a.dollarRisk)

    const total = rows.reduce((s, r) => s + r.dollarRisk, 0)
    const totalPct = equity > 0 ? (total / equity) * 100 : 0
    const largest = rows.length > 0 ? rows[0] : null

    return { totalEquity: equity, riskRows: rows, totalRisk: total, totalRiskPct: totalPct, largestRisk: largest }
  }, [openTrades, positions, accounts])

  function acctLabel(num) {
    return nicknameMap[num] || ('···' + num.slice(-4))
  }

  // Build account color map for stacked bar
  const accountColors = useMemo(() => {
    const uniqueAccounts = [...new Set(riskRows.map(r => r.account))]
    const map = {}
    uniqueAccounts.forEach((a, i) => { map[a] = CHART_COLORS[i % CHART_COLORS.length] })
    return map
  }, [riskRows])

  return (
    <div>
      <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Portfolio at Risk</h2>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="card">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Total $ at Risk</div>
          <div className="font-mono text-xl font-semibold text-ember">
            <PrivateValue>{fmtCurrency(totalRisk)}</PrivateValue>
          </div>
        </div>
        <div className="card">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Positions w/ Stops</div>
          <div className="font-mono text-xl font-semibold text-ink">{riskRows.length}</div>
        </div>
        <div className="card">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">% of Portfolio</div>
          <div className="font-mono text-xl font-semibold text-ember">
            <PrivateValue>{totalRiskPct.toFixed(2)}%</PrivateValue>
          </div>
        </div>
        <div className="card">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Largest Position Risk</div>
          <div className="font-mono text-xl font-semibold text-ember">
            <PrivateValue>{largestRisk ? fmtCurrency(largestRisk.dollarRisk) : '—'}</PrivateValue>
          </div>
          {largestRisk && <div className="font-mono text-[10px] text-inkMuted mt-1">{largestRisk.symbol}</div>}
        </div>
      </div>

      {/* Stacked bar */}
      {riskRows.length > 0 && (
        <div className="card mb-4">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Risk by Position</div>
          <div className="flex h-6 rounded-lg overflow-hidden">
            {riskRows.map(r => (
              <div
                key={r.id}
                className="h-full relative group"
                style={{
                  width: `${Math.max(r.pctOfPortfolio / totalRiskPct * 100, 1)}%`,
                  backgroundColor: accountColors[r.account],
                }}
                title={`${r.symbol} — ${r.pctOfPortfolio.toFixed(2)}%`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-2">
            {[...new Set(riskRows.map(r => r.account))].map(a => (
              <div key={a} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: accountColors[a] }} />
                <span className="font-mono text-[10px] text-inkMuted">{acctLabel(a)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk table */}
      {riskRows.length > 0 ? (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-border">
                  {['Symbol', 'Account', 'Side', 'Entry', 'Stop', 'Shares', '$ at Risk', '% Portfolio'].map(h => (
                    <th key={h} className={`px-3 py-2.5 font-mono text-[10px] tracking-widest uppercase text-inkMuted ${
                      ['Entry', 'Stop', 'Shares', '$ at Risk', '% Portfolio'].includes(h) ? 'text-right' : 'text-left'
                    }`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {riskRows.map((r, i) => (
                  <tr key={r.id} className={`border-b border-border last:border-0 ${i % 2 === 1 ? 'bg-canvas2/20' : ''}`}>
                    <td className="px-3 py-2.5 font-black text-xs tracking-wide">{r.symbol}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-inkMuted">{acctLabel(r.account)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`font-mono text-[10px] font-semibold ${r.side === 'long' ? 'text-forest' : 'text-ember'}`}>
                        {r.side?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs"><PrivateValue>{fmtCurrency(r.entry)}</PrivateValue></td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs"><PrivateValue>{fmtCurrency(r.stop)}</PrivateValue></td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{r.shares}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-ember"><PrivateValue>{fmtCurrency(r.dollarRisk)}</PrivateValue></td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-ember"><PrivateValue>{r.pctOfPortfolio.toFixed(2)}%</PrivateValue></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card text-center py-8">
          <div className="font-mono text-xs text-inkMuted">No open positions with stop losses set</div>
        </div>
      )}

      <div className="flex items-start gap-1.5 mt-3">
        <Info size={12} className="text-inkMuted mt-0.5 shrink-0" />
        <span className="font-mono text-[10px] text-inkMuted">Based on current stop loss prices. Positions without stops are excluded.</span>
      </div>
    </div>
  )
}

// ── Section 5: Benchmark Comparison ──────────────────────────────────────────

const COLOR_STEEL_BLUE = '#5b7ea1'
const COLOR_GOLD = '#c4953a'

function BenchmarkComparison({ trades, effStart, effEnd, totalEquity }) {
  const [benchData, setBenchData] = useState(null)
  const [benchLoading, setBenchLoading] = useState(false)
  const [benchError, setBenchError] = useState(null)

  const closed = useMemo(() => {
    return [...trades]
      .filter(t => t.status === 'closed' && t.net_pnl != null && t.exit_date)
      .sort((a, b) => a.exit_date.localeCompare(b.exit_date))
  }, [trades])

  // Determine date range for benchmark fetch
  const { fetchFrom, fetchTo } = useMemo(() => {
    if (closed.length === 0) return { fetchFrom: null, fetchTo: null }
    const from = effStart || closed[0].exit_date
    const to = effEnd || closed[closed.length - 1].exit_date
    return { fetchFrom: from, fetchTo: to }
  }, [closed, effStart, effEnd])

  // Fetch SPY and QQQ data
  useEffect(() => {
    if (!fetchFrom || !fetchTo) return
    let cancelled = false
    async function fetchBenchmarks() {
      setBenchLoading(true)
      setBenchError(null)
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) throw new Error('Missing Supabase session')
        const authHeaders = { Authorization: `Bearer ${session.access_token}` }
        const [spyRes, qqqRes] = await Promise.all([
          fetch(`/api/ibkr/candles?symbol=SPY&barSize=${encodeURIComponent('1 day')}&duration=${encodeURIComponent('1 Y')}`, { headers: authHeaders }),
          fetch(`/api/ibkr/candles?symbol=QQQ&barSize=${encodeURIComponent('1 day')}&duration=${encodeURIComponent('1 Y')}`, { headers: authHeaders }),
        ])
        if (!spyRes.ok || !qqqRes.ok) throw new Error('Failed to fetch')
        const [spyData, qqqData] = await Promise.all([spyRes.json(), qqqRes.json()])
        if (cancelled) return

        // Build date-keyed maps of close prices
        // IBKR daily time format: "20260402" → "2026-04-02"
        const ibkrDateToISO = t => typeof t === 'string' && !t.includes('-') ? `${t.slice(0,4)}-${t.slice(4,6)}-${t.slice(6,8)}` : fmtISO(new Date(t * 1000))
        const spyMap = {}
        for (const c of (spyData.bars || [])) {
          spyMap[ibkrDateToISO(c.time)] = c.close
        }
        const qqqMap = {}
        for (const c of (qqqData.bars || [])) {
          qqqMap[ibkrDateToISO(c.time)] = c.close
        }

        setBenchData({ spyMap, qqqMap })
      } catch (err) {
        if (!cancelled) setBenchError('Benchmark data unavailable')
      } finally {
        if (!cancelled) setBenchLoading(false)
      }
    }
    fetchBenchmarks()
    return () => { cancelled = true }
  }, [fetchFrom, fetchTo])

  // Build combined chart data
  const { chartData, portfolioReturn, spyReturn, qqqReturn } = useMemo(() => {
    if (!benchData || closed.length === 0) return { chartData: [], portfolioReturn: 0, spyReturn: 0, qqqReturn: 0 }

    const { spyMap, qqqMap } = benchData

    // Portfolio cumulative % return by date
    const dailyPnlMap = {}
    for (const t of closed) {
      dailyPnlMap[t.exit_date] = (dailyPnlMap[t.exit_date] || 0) + Number(t.net_pnl)
    }

    // Get all unique dates across portfolio + benchmarks, sorted
    const allDates = [...new Set([
      ...Object.keys(dailyPnlMap),
      ...Object.keys(spyMap),
      ...Object.keys(qqqMap),
    ])].sort()

    // Find first benchmark prices
    const spyDates = Object.keys(spyMap).sort()
    const qqqDates = Object.keys(qqqMap).sort()
    const spyBase = spyDates.length > 0 ? spyMap[spyDates[0]] : null
    const qqqBase = qqqDates.length > 0 ? qqqMap[qqqDates[0]] : null

    let cumPnl = 0
    // Starting equity = current equity minus total net P&L from closed trades
    const totalNetPnl = closed.reduce((s, t) => s + Number(t.net_pnl), 0)
    const portfolioBase = (totalEquity || 0) - totalNetPnl

    const data = []
    for (const date of allDates) {
      if (dailyPnlMap[date]) cumPnl += dailyPnlMap[date]
      const portfolioPct = portfolioBase > 0 ? (cumPnl / portfolioBase) * 100 : 0
      const spyPct = spyBase && spyMap[date] ? ((spyMap[date] - spyBase) / spyBase) * 100 : null
      const qqqPct = qqqBase && qqqMap[date] ? ((qqqMap[date] - qqqBase) / qqqBase) * 100 : null

      data.push({ date, portfolio: portfolioPct, spy: spyPct, qqq: qqqPct })
    }

    const lastPortfolio = data.length > 0 ? data[data.length - 1].portfolio : 0
    const lastSpy = spyBase && spyDates.length > 0 ? ((spyMap[spyDates[spyDates.length - 1]] - spyBase) / spyBase) * 100 : 0
    const lastQqq = qqqBase && qqqDates.length > 0 ? ((qqqMap[qqqDates[qqqDates.length - 1]] - qqqBase) / qqqBase) * 100 : 0

    return { chartData: data, portfolioReturn: lastPortfolio, spyReturn: lastSpy, qqqReturn: lastQqq }
  }, [benchData, closed])

  const alphaSpy = portfolioReturn - spyReturn
  const alphaQqq = portfolioReturn - qqqReturn

  if (closed.length === 0) {
    return (
      <div>
        <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Benchmark Comparison</h2>
        <div className="card text-center py-12">
          <div className="font-mono text-xs text-inkMuted">No closed trades to compare</div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Benchmark Comparison</h2>
      <div className="card">
        {/* Summary stats */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <PrivateValue>
            <StatBadge label="Portfolio" value={fmtPct(portfolioReturn)} colorClass={clr(portfolioReturn)} />
          </PrivateValue>
          <StatBadge label="SPY" value={fmtPct(spyReturn)} colorClass={clr(spyReturn)} />
          <StatBadge label="QQQ" value={fmtPct(qqqReturn)} colorClass={clr(qqqReturn)} />
          <PrivateValue>
            <StatBadge label="Alpha vs SPY" value={fmtPct(alphaSpy)} colorClass={clr(alphaSpy)} />
          </PrivateValue>
          <PrivateValue>
            <StatBadge label="Alpha vs QQQ" value={fmtPct(alphaQqq)} colorClass={clr(alphaQqq)} />
          </PrivateValue>
        </div>

        {benchLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="font-mono text-xs text-inkMuted animate-pulse">Loading benchmark data…</div>
          </div>
        )}

        {benchError && (
          <div className="flex items-center justify-center py-16">
            <div className="font-mono text-xs text-inkMuted">{benchError}</div>
          </div>
        )}

        {!benchLoading && !benchError && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                axisLine={{ stroke: 'rgb(var(--color-border))' }}
                tickLine={false}
                minTickGap={60}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => `${v.toFixed(0)}%`}
                width={45}
              />
              <ReferenceLine y={0} stroke="rgb(var(--color-border))" strokeDasharray="3 3" />
              <Tooltip content={<BenchmarkTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 11, fontFamily: '"DM Sans"' }}
                iconType="line"
              />
              <Line type="monotone" dataKey="portfolio" name="Portfolio" stroke="rgb(var(--color-forest))" strokeWidth={2} dot={false} connectNulls />
              <Line type="monotone" dataKey="spy" name="SPY" stroke={COLOR_STEEL_BLUE} strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 2" />
              <Line type="monotone" dataKey="qqq" name="QQQ" stroke={COLOR_GOLD} strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

function BenchmarkTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-canvas border border-border rounded-lg px-3 py-2 shadow-lg">
      <div className="font-mono text-[10px] text-inkMuted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="font-mono text-xs font-semibold flex items-center gap-1.5" style={{ color: p.color }}>
          <span>{p.name}:</span>
          <span>{p.value != null ? fmtPct(p.value) : '—'}</span>
        </div>
      ))}
    </div>
  )
}

// ── Section 6: Trade Analytics ───────────────────────────────────────────────

function TradeAnalyticsSection({ trades }) {
  const closed = useMemo(() => {
    return trades.filter(t => t.status === 'closed' && t.net_pnl != null)
  }, [trades])

  // P&L by Hour of Day
  const hourData = useMemo(() => {
    const hours = {}
    for (let h = 6; h <= 20; h++) hours[h] = { hour: h, pnl: 0, count: 0, wins: 0 }

    for (const t of closed) {
      if (!t.entry_time) continue
      const h = parseInt(t.entry_time.split(':')[0], 10)
      if (h < 6 || h > 20) continue
      hours[h].pnl += Number(t.net_pnl)
      hours[h].count++
      if (Number(t.net_pnl) > 0) hours[h].wins++
    }

    return Object.values(hours).map(d => ({
      ...d,
      label: `${d.hour > 12 ? d.hour - 12 : d.hour}${d.hour >= 12 ? 'PM' : 'AM'}`,
      winRate: d.count > 0 ? ((d.wins / d.count) * 100).toFixed(0) : '0',
    }))
  }, [closed])

  // P&L by Day of Week
  const dayData = useMemo(() => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const map = {}
    for (let i = 0; i < 7; i++) map[i] = { dayIdx: i, day: days[i], pnl: 0, count: 0, wins: 0 }

    for (const t of closed) {
      if (!t.entry_date) continue
      const d = new Date(t.entry_date + 'T12:00:00')
      const dow = d.getDay()
      map[dow].pnl += Number(t.net_pnl)
      map[dow].count++
      if (Number(t.net_pnl) > 0) map[dow].wins++
    }

    return [1, 2, 3, 4, 5].map(i => ({
      ...map[i],
      winRate: map[i].count > 0 ? ((map[i].wins / map[i].count) * 100).toFixed(0) : '0',
    }))
  }, [closed])

  // Scatter data (duration vs P&L)
  const scatterData = useMemo(() => {
    return closed
      .filter(t => t.duration_minutes != null && t.duration_minutes > 0)
      .map(t => ({
        duration: Number(t.duration_minutes),
        pnl: Number(t.net_pnl),
        symbol: t.symbol,
        rMultiple: t.r_multiple != null ? Number(t.r_multiple).toFixed(2) : '—',
        positive: Number(t.net_pnl) > 0,
      }))
  }, [closed])

  function fmtDuration(mins) {
    if (mins < 60) return `${Math.round(mins)}m`
    if (mins < 1440) return `${Math.round(mins / 60)}h`
    return `${Math.round(mins / 1440)}d`
  }

  if (closed.length === 0) {
    return (
      <div>
        <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Trade Analytics</h2>
        <div className="card text-center py-12">
          <div className="font-mono text-xs text-inkMuted">No closed trades to analyze</div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Trade Analytics</h2>

      {/* Two charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* P&L by Hour */}
        <div className="card">
          <div className="font-mono text-xs font-semibold text-ink mb-4">P&L by Hour of Day</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hourData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 9, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                axisLine={{ stroke: 'rgb(var(--color-border))' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => v >= 1000 || v <= -1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                width={45}
              />
              <ReferenceLine y={0} stroke="rgb(var(--color-border))" />
              <Tooltip content={<HourDayTooltip />} />
              <Bar dataKey="pnl" radius={[3, 3, 0, 0]}>
                {hourData.map((d, i) => (
                  <Cell key={i} fill={d.pnl >= 0 ? 'rgb(var(--color-forest))' : 'rgb(var(--color-ember))'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* P&L by Day of Week */}
        <div className="card">
          <div className="font-mono text-xs font-semibold text-ink mb-4">P&L by Day of Week</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dayData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
              <XAxis
                dataKey="day"
                tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                axisLine={{ stroke: 'rgb(var(--color-border))' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => v >= 1000 || v <= -1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                width={45}
              />
              <ReferenceLine y={0} stroke="rgb(var(--color-border))" />
              <Tooltip content={<HourDayTooltip />} />
              <Bar dataKey="pnl" radius={[3, 3, 0, 0]}>
                {dayData.map((d, i) => (
                  <Cell key={i} fill={d.pnl >= 0 ? 'rgb(var(--color-forest))' : 'rgb(var(--color-ember))'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Duration Scatter */}
      <div className="card">
        <div className="font-mono text-xs font-semibold text-ink mb-4">Trade Duration vs P&L</div>
        {scatterData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
              <XAxis
                dataKey="duration"
                name="Duration"
                scale="log"
                domain={['dataMin', 'dataMax']}
                tick={{ fontSize: 9, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                axisLine={{ stroke: 'rgb(var(--color-border))' }}
                tickLine={false}
                tickFormatter={v => fmtDuration(v)}
                type="number"
              />
              <YAxis
                dataKey="pnl"
                name="P&L"
                tick={{ fontSize: 9, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => v >= 1000 || v <= -1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                width={50}
              />
              <ZAxis range={[20, 20]} />
              <ReferenceLine y={0} stroke="rgb(var(--color-border))" strokeDasharray="3 3" />
              <Tooltip content={<ScatterTooltipContent fmtDur={fmtDuration} />} />
              <Scatter data={scatterData.filter(d => d.positive)} fill="rgb(var(--color-forest))" opacity={0.7} />
              <Scatter data={scatterData.filter(d => !d.positive)} fill="rgb(var(--color-ember))" opacity={0.7} />
            </ScatterChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 font-mono text-xs text-inkMuted">No duration data available</div>
        )}
      </div>
    </div>
  )
}

function HourDayTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const d = payload[0]?.payload
  if (!d) return null
  return (
    <div className="bg-canvas border border-border rounded-lg px-3 py-2 shadow-lg">
      <div className="font-mono text-[10px] text-inkMuted mb-1">{d.label || d.day}</div>
      <div className={`font-mono text-xs font-semibold ${d.pnl >= 0 ? 'text-forest' : 'text-ember'}`}>
        P&L: {fmtCurrency(d.pnl)}
      </div>
      <div className="font-mono text-[10px] text-inkMuted">Trades: {d.count}</div>
      <div className="font-mono text-[10px] text-inkMuted">Win Rate: {d.winRate}%</div>
    </div>
  )
}

function ScatterTooltipContent({ active, payload, fmtDur }) {
  if (!active || !payload?.length) return null
  const d = payload[0]?.payload
  if (!d) return null
  return (
    <div className="bg-canvas border border-border rounded-lg px-3 py-2 shadow-lg">
      <div className="font-mono text-xs font-semibold text-ink mb-1">{d.symbol}</div>
      <div className="font-mono text-[10px] text-inkMuted">Duration: {fmtDur(d.duration)}</div>
      <div className={`font-mono text-[10px] font-semibold ${d.pnl >= 0 ? 'text-forest' : 'text-ember'}`}>
        P&L: {fmtCurrency(d.pnl)}
      </div>
      <div className="font-mono text-[10px] text-inkMuted">R-Multiple: {d.rMultiple}</div>
    </div>
  )
}

// ── Section 7: Segmented Performance ─────────────────────────────────────────

function SegmentedPerformance({ trades }) {
  const closed = useMemo(() => {
    return trades.filter(t => t.status === 'closed' && t.net_pnl != null)
  }, [trades])

  // P&L by Symbol (top 10 by absolute value)
  const symbolData = useMemo(() => {
    const map = {}
    for (const t of closed) {
      const s = t.symbol || 'Unknown'
      if (!map[s]) map[s] = { symbol: s, displaySymbol: fmtSymbol(s, t.asset_type), pnl: 0, count: 0 }
      map[s].pnl += Number(t.net_pnl)
      map[s].count++
    }
    return Object.values(map)
      .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
      .slice(0, 10)
  }, [closed])

  // P&L by Asset Type
  const typeData = useMemo(() => {
    const map = {}
    for (const t of closed) {
      const type = t.asset_type === 'etf' ? 'ETF' : t.asset_type ? t.asset_type.charAt(0).toUpperCase() + t.asset_type.slice(1) : 'Other'
      if (!map[type]) map[type] = { type, pnl: 0, count: 0, wins: 0 }
      map[type].pnl += Number(t.net_pnl)
      map[type].count++
      if (Number(t.net_pnl) > 0) map[type].wins++
    }
    return Object.values(map).map(d => ({
      ...d,
      winRate: d.count > 0 ? ((d.wins / d.count) * 100).toFixed(0) : '0',
    }))
  }, [closed])

  if (closed.length === 0) {
    return (
      <div>
        <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Segmented Performance</h2>
        <div className="card text-center py-12">
          <div className="font-mono text-xs text-inkMuted">No closed trades to segment</div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Segmented Performance</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* P&L by Symbol */}
        <div className="card">
          <div className="font-mono text-xs font-semibold text-ink mb-4">P&L by Symbol (Top 10)</div>
          <ResponsiveContainer width="100%" height={Math.max(symbolData.length * 32, 150)}>
            <BarChart data={symbolData} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
              <XAxis
                type="number"
                tick={{ fontSize: 9, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                axisLine={{ stroke: 'rgb(var(--color-border))' }}
                tickLine={false}
                tickFormatter={v => v >= 1000 || v <= -1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
              />
              <YAxis
                dataKey="displaySymbol"
                type="category"
                tick={{ fontSize: 10, fill: 'rgb(var(--color-ink))', fontFamily: '"DM Sans"', fontWeight: 700 }}
                axisLine={false}
                tickLine={false}
                width={100}
              />
              <ReferenceLine x={0} stroke="rgb(var(--color-border))" />
              <Tooltip content={<SymbolTooltip />} />
              <Bar dataKey="pnl" radius={[0, 3, 3, 0]}>
                {symbolData.map((d, i) => (
                  <Cell key={i} fill={d.pnl >= 0 ? 'rgb(var(--color-forest))' : 'rgb(var(--color-ember))'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* P&L by Asset Type */}
        <div className="card">
          <div className="font-mono text-xs font-semibold text-ink mb-4">P&L by Asset Type</div>
          <ResponsiveContainer width="100%" height={Math.max(typeData.length * 48, 150)}>
            <BarChart data={typeData} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
              <XAxis
                type="number"
                tick={{ fontSize: 9, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }}
                axisLine={{ stroke: 'rgb(var(--color-border))' }}
                tickLine={false}
                tickFormatter={v => v >= 1000 || v <= -1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
              />
              <YAxis
                dataKey="type"
                type="category"
                tick={{ fontSize: 10, fill: 'rgb(var(--color-ink))', fontFamily: '"DM Sans"', fontWeight: 700 }}
                axisLine={false}
                tickLine={false}
                width={65}
              />
              <ReferenceLine x={0} stroke="rgb(var(--color-border))" />
              <Tooltip content={<TypeTooltip />} />
              <Bar dataKey="pnl" radius={[0, 3, 3, 0]}>
                {typeData.map((d, i) => (
                  <Cell key={i} fill={d.pnl >= 0 ? 'rgb(var(--color-forest))' : 'rgb(var(--color-ember))'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

function SymbolTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const d = payload[0]?.payload
  if (!d) return null
  return (
    <div className="bg-canvas border border-border rounded-lg px-3 py-2 shadow-lg">
      <div className="font-mono text-xs font-semibold text-ink mb-1">{d.displaySymbol}</div>
      <div className={`font-mono text-xs font-semibold ${d.pnl >= 0 ? 'text-forest' : 'text-ember'}`}>
        P&L: {fmtSigned(d.pnl)}
      </div>
      <div className="font-mono text-[10px] text-inkMuted">{d.count} trade{d.count !== 1 ? 's' : ''}</div>
    </div>
  )
}

function TypeTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const d = payload[0]?.payload
  if (!d) return null
  return (
    <div className="bg-canvas border border-border rounded-lg px-3 py-2 shadow-lg">
      <div className="font-mono text-xs font-semibold text-ink mb-1">{d.type}</div>
      <div className={`font-mono text-xs font-semibold ${d.pnl >= 0 ? 'text-forest' : 'text-ember'}`}>
        P&L: {fmtSigned(d.pnl)}
      </div>
      <div className="font-mono text-[10px] text-inkMuted">{d.count} trades · {d.winRate}% win rate</div>
    </div>
  )
}

// ── Section 8: Calendar Heatmap ──────────────────────────────────────────────

function CalendarHeatmap({ trades }) {
  const closed = useMemo(() => {
    return trades.filter(t => t.status === 'closed' && t.net_pnl != null && t.exit_date)
  }, [trades])

  const now = new Date()
  const [viewYear, setViewYear] = useState(now.getFullYear())
  const [viewMonth, setViewMonth] = useState(now.getMonth())
  const [popover, setPopover] = useState(null) // { date, x, y }

  // Daily P&L map
  const dailyMap = useMemo(() => {
    const map = {}
    for (const t of closed) {
      const d = t.exit_date
      if (!map[d]) map[d] = { pnl: 0, count: 0, symbols: [] }
      map[d].pnl += Number(t.net_pnl)
      map[d].count++
      if (!map[d].symbols.includes(t.symbol)) map[d].symbols.push(t.symbol)
    }
    return map
  }, [closed])

  // Build calendar grid
  const { weeks, monthLabel, monthStats } = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1)
    const lastDay = new Date(viewYear, viewMonth + 1, 0)
    const startDow = firstDay.getDay() // 0=Sun
    const daysInMonth = lastDay.getDate()

    const label = firstDay.toLocaleString('en-US', { month: 'long', year: 'numeric' })

    // Build flat array of day cells
    const cells = []
    // Leading blanks
    for (let i = 0; i < startDow; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      cells.push({ day: d, date: dateStr, data: dailyMap[dateStr] || null })
    }
    // Trailing blanks to fill last week
    while (cells.length % 7 !== 0) cells.push(null)

    // Chunk into weeks
    const wks = []
    for (let i = 0; i < cells.length; i += 7) wks.push(cells.slice(i, i + 7))

    // Month stats
    let totalPnl = 0, tradingDays = 0, winDays = 0, lossDays = 0
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      const data = dailyMap[dateStr]
      if (data) {
        totalPnl += data.pnl
        tradingDays++
        if (data.pnl > 0) winDays++
        else if (data.pnl < 0) lossDays++
      }
    }

    return { weeks: wks, monthLabel: label, monthStats: { totalPnl, tradingDays, winDays, lossDays } }
  }, [viewYear, viewMonth, dailyMap])

  // Max magnitude for color scaling
  const maxMag = useMemo(() => {
    let max = 0
    for (const w of weeks) {
      for (const cell of w) {
        if (cell?.data) max = Math.max(max, Math.abs(cell.data.pnl))
      }
    }
    return max || 1
  }, [weeks])

  function getCellBg(data) {
    if (!data) return 'transparent'
    const intensity = Math.min(Math.abs(data.pnl) / maxMag, 1)
    const opacity = 0.15 + intensity * 0.55
    if (data.pnl > 0) return `color-mix(in srgb, rgb(var(--color-forest)) ${Math.round(opacity * 100)}%, transparent)`
    if (data.pnl < 0) return `color-mix(in srgb, rgb(var(--color-ember)) ${Math.round(opacity * 100)}%, transparent)`
    return 'transparent'
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11) }
    else setViewMonth(m => m - 1)
    setPopover(null)
  }

  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0) }
    else setViewMonth(m => m + 1)
    setPopover(null)
  }

  function handleCellClick(cell, e) {
    if (!cell?.data) { setPopover(null); return }
    setPopover(prev => prev?.date === cell.date ? null : { date: cell.date, data: cell.data })
  }

  return (
    <div>
      <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Calendar Heatmap</h2>
      <div className="card">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-canvas2 text-inkMuted hover:text-ink transition-colors">
            <ChevronLeft size={18} />
          </button>
          <span className="font-mono text-sm font-semibold text-ink">{monthLabel}</span>
          <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-canvas2 text-inkMuted hover:text-ink transition-colors">
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="text-center font-mono text-[9px] uppercase tracking-widest text-inkMuted py-1">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1 relative">
          {weeks.flat().map((cell, i) => (
            <div
              key={i}
              onClick={e => handleCellClick(cell, e)}
              className={`aspect-square rounded-lg flex flex-col items-center justify-center transition-colors ${
                cell?.data ? 'cursor-pointer hover:ring-1 hover:ring-ink/20' : ''
              }`}
              style={{ backgroundColor: getCellBg(cell?.data) }}
            >
              {cell && (
                <>
                  <span className="font-mono text-[10px] text-ink leading-none">{cell.day}</span>
                  {cell.data && (
                    <>
                      <span className={`font-mono text-[8px] font-semibold leading-tight mt-0.5 ${cell.data.pnl >= 0 ? 'text-forest' : 'text-ember'}`}>
                        <PrivateValue placeholder="••">
                          {cell.data.pnl >= 0 ? '+' : ''}{Math.abs(cell.data.pnl) >= 1000 ? `$${(cell.data.pnl / 1000).toFixed(1)}k` : `$${Math.round(cell.data.pnl)}`}
                        </PrivateValue>
                      </span>
                      <span className="font-mono text-[7px] text-inkMuted leading-tight">{cell.data.count}t</span>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* Popover */}
        {popover && (
          <div className="mt-3 bg-canvas2 border border-border rounded-lg px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs font-semibold text-ink">{popover.date}</span>
              <button onClick={() => setPopover(null)} className="font-mono text-[10px] text-inkMuted hover:text-ink">Close</button>
            </div>
            <div className={`font-mono text-sm font-semibold ${popover.data.pnl >= 0 ? 'text-forest' : 'text-ember'}`}>
              <PrivateValue>{fmtSigned(popover.data.pnl)}</PrivateValue>
            </div>
            <div className="font-mono text-[10px] text-inkMuted mt-1">{popover.data.count} trade{popover.data.count !== 1 ? 's' : ''}</div>
            <div className="font-mono text-[10px] text-inkMuted mt-1">
              {popover.data.symbols.slice(0, 10).join(', ')}{popover.data.symbols.length > 10 ? ` +${popover.data.symbols.length - 10} more` : ''}
            </div>
          </div>
        )}

        {/* Monthly summary */}
        <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-border">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">Month P&L:</span>
            <span className={`font-mono text-xs font-semibold ${clr(monthStats.totalPnl)}`}>
              <PrivateValue>{fmtSigned(monthStats.totalPnl)}</PrivateValue>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">Trading Days:</span>
            <span className="font-mono text-xs font-semibold text-ink">{monthStats.tradingDays}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">Win Days:</span>
            <span className="font-mono text-xs font-semibold text-forest">{monthStats.winDays}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">Loss Days:</span>
            <span className="font-mono text-xs font-semibold text-ember">{monthStats.lossDays}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Portfolio View ────────────────────────────────────────────────────────────

function PortfolioView({ snapshots, selectedAccounts, effStart, effEnd, nicknameMap, onMetricClick, onMetricsComputed, onBenchmarkMetricsComputed }) {
  // ── Process snapshots: account filter + date range ──
  const equitySeries = useMemo(() => {
    return snapshots.map(s => {
      let equity
      if (selectedAccounts.length > 0 && Array.isArray(s.account_snapshots)) {
        equity = s.account_snapshots
          .filter(a => selectedAccounts.includes(a.account_number))
          .reduce((sum, a) => sum + (Number(a.equity) || 0), 0)
      } else {
        equity = Number(s.total_equity) || 0
      }
      return { date: s.snapshot_date, equity }
    })
  }, [snapshots, selectedAccounts])

  const filteredSeries = useMemo(() => {
    return equitySeries.filter(d => {
      if (effStart && d.date < effStart) return false
      if (effEnd && d.date > effEnd) return false
      return true
    })
  }, [equitySeries, effStart, effEnd])

  // ── Compute portfolio metrics ──
  const metrics = useMemo(() => {
    if (filteredSeries.length < 2) return null

    const first = filteredSeries[0].equity
    const last = filteredSeries[filteredSeries.length - 1].equity
    const cumReturn = (last / first) - 1
    const days = (new Date(filteredSeries[filteredSeries.length - 1].date) - new Date(filteredSeries[0].date)) / 86400000
    const annReturn = days > 30 ? Math.pow(1 + cumReturn, 365 / days) - 1 : null
    const isAnnualized = days > 30

    // Daily returns
    const dailyReturns = []
    for (let i = 1; i < filteredSeries.length; i++) {
      const prev = filteredSeries[i - 1].equity
      if (prev > 0) dailyReturns.push((filteredSeries[i].equity - prev) / prev)
    }

    // Volatility (annualized)
    const mean = dailyReturns.length > 0 ? dailyReturns.reduce((s, r) => s + r, 0) / dailyReturns.length : 0
    const variance = dailyReturns.length > 1
      ? dailyReturns.reduce((s, r) => s + (r - mean) ** 2, 0) / (dailyReturns.length - 1)
      : 0
    const volatility = Math.sqrt(variance) * Math.sqrt(252)

    // Sharpe (risk-free = 5%)
    const rf = 0.05
    const effectiveAnnReturn = annReturn != null ? annReturn : cumReturn
    const sharpe = dailyReturns.length >= 5 && volatility > 0 ? (effectiveAnnReturn - rf) / volatility : null

    // Sortino (downside deviation)
    const negReturns = dailyReturns.filter(r => r < 0)
    const downsideVar = negReturns.length > 1
      ? negReturns.reduce((s, r) => s + r ** 2, 0) / (negReturns.length - 1)
      : 0
    const downsideDev = Math.sqrt(downsideVar) * Math.sqrt(252)
    const sortino = dailyReturns.length >= 5 && downsideDev > 0 ? (effectiveAnnReturn - rf) / downsideDev : null

    // Max drawdown
    let peak = filteredSeries[0].equity
    let maxDD = 0, maxDDDollar = 0
    let peakDate = filteredSeries[0].date, troughDate = filteredSeries[0].date
    let currentDD = 0

    for (const d of filteredSeries) {
      if (d.equity > peak) {
        peak = d.equity
        peakDate = d.date
      }
      const dd = (d.equity - peak) / peak
      if (dd < maxDD) {
        maxDD = dd
        maxDDDollar = d.equity - peak
        troughDate = d.date
      }
      currentDD = dd
    }

    // Calmar
    const calmar = annReturn != null && maxDD < 0 ? annReturn / Math.abs(maxDD) : null

    return {
      cumReturn, annReturn, isAnnualized, volatility,
      sharpe, sortino, maxDD, maxDDDollar, currentDD,
      calmar, peakDate, troughDate,
      firstDate: filteredSeries[0].date,
      lastDate: filteredSeries[filteredSeries.length - 1].date,
      firstEquity: first, lastEquity: last,
    }
  }, [filteredSeries])

  // Report portfolio metrics for capture
  useEffect(() => {
    if (onMetricsComputed && metrics) {
      onMetricsComputed({
        total_return: metrics.cumReturn * 100,
        annualized_return: metrics.annReturn != null ? metrics.annReturn * 100 : null,
        volatility: metrics.volatility * 100,
        sharpe_ratio: metrics.sharpe,
        sortino_ratio: metrics.sortino,
        calmar_ratio: metrics.calmar,
        max_drawdown_pct: Math.abs(metrics.maxDD * 100),
      })
    }
  }, [metrics, onMetricsComputed])

  // ── Period return badges ──
  const periodReturns = useMemo(() => {
    if (equitySeries.length < 2) return {}
    const last = equitySeries[equitySeries.length - 1]
    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth()

    function returnSince(startDate) {
      const from = equitySeries.find(d => d.date >= startDate)
      if (!from || from.equity <= 0) return null
      return ((last.equity / from.equity) - 1) * 100
    }

    return {
      '1M': returnSince(fmtISO(new Date(y, m - 1, now.getDate()))),
      '3M': returnSince(fmtISO(new Date(y, m - 3, now.getDate()))),
      'YTD': returnSince(fmtISO(new Date(y, 0, 1))),
      'ALL': equitySeries[0].equity > 0 ? ((last.equity / equitySeries[0].equity) - 1) * 100 : null,
    }
  }, [equitySeries])

  // ── Drawdown series for chart ──
  const drawdownSeries = useMemo(() => {
    if (filteredSeries.length < 2) return []
    let peak = filteredSeries[0].equity
    return filteredSeries.map(d => {
      if (d.equity > peak) peak = d.equity
      return { date: d.date, dd: peak > 0 ? ((d.equity - peak) / peak) * 100 : 0 }
    })
  }, [filteredSeries])

  // ── Chart formatting helpers ──
  const chartData = useMemo(() => {
    const rangeDays = filteredSeries.length > 1
      ? (new Date(filteredSeries[filteredSeries.length - 1].date) - new Date(filteredSeries[0].date)) / 86400000
      : 0
    const longRange = rangeDays > 180

    return filteredSeries.map(d => ({
      date: new Date(d.date).toLocaleDateString('en-US', longRange
        ? { month: 'short', year: '2-digit' }
        : { month: 'short', day: 'numeric' }),
      equity: d.equity,
      rawDate: d.date,
    }))
  }, [filteredSeries])

  const ddChartData = useMemo(() => {
    const rangeDays = drawdownSeries.length > 1
      ? (new Date(drawdownSeries[drawdownSeries.length - 1].date) - new Date(drawdownSeries[0].date)) / 86400000
      : 0
    const longRange = rangeDays > 180

    return drawdownSeries.map(d => ({
      date: new Date(d.date).toLocaleDateString('en-US', longRange
        ? { month: 'short', year: '2-digit' }
        : { month: 'short', day: 'numeric' }),
      dd: d.dd,
    }))
  }, [drawdownSeries])

  const equityDomain = useMemo(() => {
    if (chartData.length < 2) return undefined
    const vals = chartData.map(d => d.equity)
    const min = Math.min(...vals)
    const max = Math.max(...vals)
    const pad = (max - min) * 0.02 || 1000
    return [Math.floor(min - pad), Math.ceil(max + pad)]
  }, [chartData])

  const acctCount = selectedAccounts.length > 0 ? selectedAccounts.length : '—'

  // ── Empty state ──
  if (filteredSeries.length < 2) {
    return (
      <div className="space-y-8">
        <div className="bg-canvas border border-border rounded-xl px-6 py-16 text-center">
          <LineChartIcon size={32} className="text-border mx-auto mb-3" strokeWidth={1} />
          <div className="font-mono text-xs text-border text-center max-w-sm mx-auto leading-relaxed">
            Portfolio analytics require at least 2 daily snapshots.
            <br />
            <span className="text-inkMuted">Data will accumulate automatically as Schwab syncs daily.</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* KPI Cards */}
      <div>
        <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Portfolio Performance</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('total_return', metrics.cumReturn * 100) : undefined}>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Total Return<InfoTooltip text="Total percentage change in portfolio value over the selected period. Includes both realized gains from closed trades and unrealized gains from open positions." /></div>
            <div className={`font-mono text-xl font-semibold ${clr(metrics.cumReturn)}`}>
              <PrivateValue>{fmtPct(metrics.cumReturn * 100)}</PrivateValue>
            </div>
            <div className="font-mono text-[10px] text-inkMuted mt-1">Since {new Date(metrics.firstDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
          </div>
          <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('annualized_return', metrics.annReturn != null ? metrics.annReturn * 100 : null) : undefined}>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Ann. Return<InfoTooltip text="Total return projected as an equivalent annual rate. Useful for comparing performance across different time periods. Requires at least 30 days of data." /></div>
            <div className={`font-mono text-xl font-semibold ${metrics.isAnnualized ? clr(metrics.annReturn) : 'text-inkMuted'}`}>
              <PrivateValue>{metrics.isAnnualized ? fmtPct(metrics.annReturn * 100) : fmtPct(metrics.cumReturn * 100)}</PrivateValue>
            </div>
            <div className="font-mono text-[10px] text-inkMuted mt-1">{metrics.isAnnualized ? 'Projected annual rate' : 'Not annualized'}</div>
          </div>
          <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('volatility', metrics.volatility * 100) : undefined}>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Volatility<InfoTooltip text="Annualized standard deviation of daily portfolio returns. Measures how much your portfolio value fluctuates. Lower volatility with similar returns indicates more consistent performance." /></div>
            <div className="font-mono text-xl font-semibold text-ink">
              {(metrics.volatility * 100).toFixed(2)}%
            </div>
            <div className="font-mono text-[10px] text-inkMuted mt-1">Annualized std dev</div>
          </div>
          <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('sharpe_ratio', metrics.sharpe) : undefined}>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Sharpe Ratio<InfoTooltip text="Risk-adjusted return: excess return (above risk-free rate) per unit of total volatility. Above 1.0 is good, above 2.0 is excellent. Investors use this to compare funds on a risk-adjusted basis." /></div>
            <div className={`font-mono text-xl font-semibold ${metrics.sharpe != null ? clr(metrics.sharpe) : 'text-inkMuted'}`}>
              {metrics.sharpe != null ? metrics.sharpe.toFixed(2) : 'N/A'}
            </div>
            <div className="font-mono text-[10px] text-inkMuted mt-1">Risk-free rate: 5%</div>
          </div>
          <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('sortino_ratio', metrics.sortino) : undefined}>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Sortino Ratio<InfoTooltip text="Similar to Sharpe but only penalizes downside volatility, ignoring upside swings. More relevant for strategies that have occasional large gains. Higher is better." /></div>
            <div className={`font-mono text-xl font-semibold ${metrics.sortino != null ? clr(metrics.sortino) : 'text-inkMuted'}`}>
              {metrics.sortino != null ? metrics.sortino.toFixed(2) : 'N/A'}
            </div>
            <div className="font-mono text-[10px] text-inkMuted mt-1">Downside risk only</div>
          </div>
          <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('max_drawdown_pct', Math.abs(metrics.maxDD * 100)) : undefined}>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Max Drawdown<InfoTooltip text="Largest peak-to-trough decline in portfolio value. This is the single most important risk metric for investors — it answers 'what's the worst it got?' A fund with -50% max drawdown lost half its value at some point." /></div>
            <div className="font-mono text-xl font-semibold text-ember">
              <PrivateValue>{fmtPct(metrics.maxDD * 100)}</PrivateValue>
            </div>
            <div className="font-mono text-[10px] text-inkMuted mt-1"><PrivateValue>{fmtCurrency(metrics.maxDDDollar)}</PrivateValue></div>
          </div>
        </div>
      </div>

      {/* Equity Curve */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div>
            <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Portfolio Value</h2>
            <div className="font-mono text-[9px] text-inkDim">Total equity across {acctCount === '—' ? 'all' : acctCount} account{acctCount !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {Object.entries(periodReturns).map(([k, v]) => (
            <StatBadge key={k} label={k} value={v != null ? <PrivateValue>{fmtPct(v)}</PrivateValue> : '—'} colorClass={v != null ? clr(v) : 'text-inkMuted'} />
          ))}
        </div>
        <div className="card">
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="portfolioEquityGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgb(var(--color-forest))" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="rgb(var(--color-forest))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }} axisLine={{ stroke: 'rgb(var(--color-border))' }} tickLine={false} minTickGap={60} />
              <YAxis domain={equityDomain} tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : v >= 1_000 ? `$${(v / 1_000).toFixed(0)}k` : `$${v}`} width={55} />
              <Tooltip content={<ChartTooltip isCurrency />} />
              <Area type="monotone" dataKey="equity" stroke="rgb(var(--color-forest))" strokeWidth={2} fill="url(#portfolioEquityGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Drawdown Chart */}
      <div>
        <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Drawdown</h2>
        <div className="font-mono text-[9px] text-inkDim mb-2">Decline from peak equity</div>
        <div className="flex flex-wrap gap-2 mb-3">
          <StatBadge label="MAX DD" value={<PrivateValue>{fmtPct(metrics.maxDD * 100)}</PrivateValue>} colorClass="text-ember" onClick={onMetricClick ? () => onMetricClick('max_drawdown_pct', Math.abs(metrics.maxDD * 100)) : undefined} />
          <StatBadge label="MAX DD $" value={<PrivateValue>{fmtCurrency(metrics.maxDDDollar)}</PrivateValue>} colorClass="text-ember" onClick={onMetricClick ? () => onMetricClick('max_drawdown_pct', Math.abs(metrics.maxDD * 100)) : undefined} />
          <StatBadge label="CURRENT" value={<PrivateValue>{fmtPct(metrics.currentDD * 100)}</PrivateValue>} colorClass={metrics.currentDD < -0.001 ? 'text-ember' : 'text-forest'} onClick={onMetricClick ? () => onMetricClick('max_drawdown_pct', Math.abs(metrics.maxDD * 100)) : undefined} />
        </div>
        <div className="card">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={ddChartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="portfolioDDGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgb(var(--color-ember))" stopOpacity={0.02} />
                  <stop offset="100%" stopColor="rgb(var(--color-ember))" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }} axisLine={{ stroke: 'rgb(var(--color-border))' }} tickLine={false} minTickGap={60} />
              <YAxis tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }} axisLine={false} tickLine={false} tickFormatter={v => v.toFixed(1) + '%'} width={45} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="dd" stroke="rgb(var(--color-ember))" strokeWidth={2} fill="url(#portfolioDDGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Benchmark Comparison */}
      <PortfolioBenchmark equitySeries={filteredSeries} effStart={effStart} effEnd={effEnd} onMetricClick={onMetricClick} onMetricsComputed={onBenchmarkMetricsComputed} />
    </div>
  )
}

// ── Stats helpers for benchmark risk metrics ─────────────────────────────────

function sampleCovariance(xs, ys) {
  const n = xs.length
  if (n < 2) return 0
  const mx = xs.reduce((a, b) => a + b, 0) / n
  const my = ys.reduce((a, b) => a + b, 0) / n
  return xs.reduce((s, x, i) => s + (x - mx) * (ys[i] - my), 0) / (n - 1)
}

function pearsonCorrelation(xs, ys) {
  const cov = sampleCovariance(xs, ys)
  const sx = Math.sqrt(sampleCovariance(xs, xs))
  const sy = Math.sqrt(sampleCovariance(ys, ys))
  if (sx === 0 || sy === 0) return 0
  return cov / (sx * sy)
}

// ── Portfolio Benchmark Comparison ────────────────────────────────────────────

function PortfolioBenchmark({ equitySeries, effStart, effEnd, onMetricClick, onMetricsComputed }) {
  const [benchData, setBenchData] = useState(null)
  const [benchLoading, setBenchLoading] = useState(false)

  const fetchFrom = effStart || (equitySeries.length > 0 ? equitySeries[0].date : null)
  const fetchTo = effEnd || (equitySeries.length > 0 ? equitySeries[equitySeries.length - 1].date : null)

  useEffect(() => {
    if (!fetchFrom || !fetchTo) return
    let cancelled = false
    async function fetchBenchmarks() {
      setBenchLoading(true)
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) throw new Error('Missing Supabase session')
        const authHeaders = { Authorization: `Bearer ${session.access_token}` }
        const [spyRes, qqqRes] = await Promise.all([
          fetch(`/api/ibkr/candles?symbol=SPY&barSize=${encodeURIComponent('1 day')}&duration=${encodeURIComponent('1 Y')}`, { headers: authHeaders }),
          fetch(`/api/ibkr/candles?symbol=QQQ&barSize=${encodeURIComponent('1 day')}&duration=${encodeURIComponent('1 Y')}`, { headers: authHeaders }),
        ])
        if (!spyRes.ok || !qqqRes.ok) throw new Error('Failed')
        const [spyData, qqqData] = await Promise.all([spyRes.json(), qqqRes.json()])
        if (cancelled) return

        const ibkrDateToISO = t => typeof t === 'string' && !t.includes('-') ? `${t.slice(0,4)}-${t.slice(4,6)}-${t.slice(6,8)}` : fmtISO(new Date(t * 1000))
        const spyMap = {}, qqqMap = {}
        for (const c of (spyData.bars || [])) { spyMap[ibkrDateToISO(c.time)] = c.close }
        for (const c of (qqqData.bars || [])) { qqqMap[ibkrDateToISO(c.time)] = c.close }
        setBenchData({ spyMap, qqqMap })
      } catch {
        setBenchData(null)
      } finally {
        if (!cancelled) setBenchLoading(false)
      }
    }
    fetchBenchmarks()
    return () => { cancelled = true }
  }, [fetchFrom, fetchTo])

  const { chartData, portfolioReturn, spyReturn, qqqReturn } = useMemo(() => {
    if (!benchData || equitySeries.length < 2) return { chartData: [], portfolioReturn: 0, spyReturn: 0, qqqReturn: 0 }

    const { spyMap, qqqMap } = benchData
    const baseEquity = equitySeries[0].equity
    const equityMap = Object.fromEntries(equitySeries.map(d => [d.date, d.equity]))

    const allDates = [...new Set([...Object.keys(equityMap), ...Object.keys(spyMap), ...Object.keys(qqqMap)])].sort()
    const spyDates = Object.keys(spyMap).sort()
    const qqqDates = Object.keys(qqqMap).sort()
    const spyBase = spyDates.length > 0 ? spyMap[spyDates[0]] : null
    const qqqBase = qqqDates.length > 0 ? qqqMap[qqqDates[0]] : null

    const data = []
    let lastEq = baseEquity
    for (const date of allDates) {
      if (equityMap[date]) lastEq = equityMap[date]
      const portPct = baseEquity > 0 ? ((lastEq - baseEquity) / baseEquity) * 100 : 0
      const spyPct = spyBase && spyMap[date] ? ((spyMap[date] - spyBase) / spyBase) * 100 : null
      const qqqPct = qqqBase && qqqMap[date] ? ((qqqMap[date] - qqqBase) / qqqBase) * 100 : null
      data.push({ date, portfolio: portPct, spy: spyPct, qqq: qqqPct })
    }

    const lastPort = data.length > 0 ? data[data.length - 1].portfolio : 0
    const lastSpy = spyBase && spyDates.length > 0 ? ((spyMap[spyDates[spyDates.length - 1]] - spyBase) / spyBase) * 100 : 0
    const lastQqq = qqqBase && qqqDates.length > 0 ? ((qqqMap[qqqDates[qqqDates.length - 1]] - qqqBase) / qqqBase) * 100 : 0

    return { chartData: data, portfolioReturn: lastPort, spyReturn: lastSpy, qqqReturn: lastQqq }
  }, [benchData, equitySeries])

  const alphaSpy = portfolioReturn - spyReturn
  const alphaQqq = portfolioReturn - qqqReturn

  // ── Risk metrics vs SPY from aligned daily returns ──
  const riskMetrics = useMemo(() => {
    if (!benchData || equitySeries.length < 3) return null

    const { spyMap } = benchData
    const spyDates = Object.keys(spyMap).sort()

    // Build SPY daily returns by date
    const spyDailyReturns = {}
    for (let i = 1; i < spyDates.length; i++) {
      const prev = spyMap[spyDates[i - 1]]
      const cur = spyMap[spyDates[i]]
      if (prev > 0) spyDailyReturns[spyDates[i]] = (cur - prev) / prev
    }

    // Build aligned return arrays (dates where both portfolio AND SPY have returns)
    const portReturns = []
    const spyReturns = []
    for (let i = 1; i < equitySeries.length; i++) {
      const date = equitySeries[i].date
      const prevEq = equitySeries[i - 1].equity
      if (prevEq <= 0) continue
      const portRet = (equitySeries[i].equity - prevEq) / prevEq
      const spyRet = spyDailyReturns[date]
      if (spyRet == null) continue
      portReturns.push(portRet)
      spyReturns.push(spyRet)
    }

    if (portReturns.length < 5) return null

    // Beta = Cov(port, spy) / Var(spy)
    const covPS = sampleCovariance(portReturns, spyReturns)
    const varSpy = sampleCovariance(spyReturns, spyReturns)
    const beta = varSpy > 0 ? covPS / varSpy : 0

    // Annualized returns for alpha
    const portCum = equitySeries[equitySeries.length - 1].equity / equitySeries[0].equity - 1
    const days = (new Date(equitySeries[equitySeries.length - 1].date) - new Date(equitySeries[0].date)) / 86400000
    const portAnn = days > 30 ? Math.pow(1 + portCum, 365 / days) - 1 : portCum

    const spyFirst = spyMap[spyDates[0]]
    const spyLast = spyMap[spyDates[spyDates.length - 1]]
    const spyCum = spyFirst > 0 ? (spyLast / spyFirst) - 1 : 0
    const spyAnn = days > 30 ? Math.pow(1 + spyCum, 365 / days) - 1 : spyCum

    // Jensen's Alpha = Ann port return - Beta × Ann SPY return
    const alpha = portAnn - beta * spyAnn

    // Correlation & R²
    const corr = pearsonCorrelation(portReturns, spyReturns)
    const rSquared = corr * corr

    // Tracking Error = Std(port - spy daily) × √252
    const excessReturns = portReturns.map((r, i) => r - spyReturns[i])
    const exMean = excessReturns.reduce((a, b) => a + b, 0) / excessReturns.length
    const exVar = excessReturns.reduce((s, r) => s + (r - exMean) ** 2, 0) / (excessReturns.length - 1)
    const trackingError = Math.sqrt(exVar) * Math.sqrt(252)

    // Information Ratio = (Ann port - Ann SPY) / Tracking Error
    const infoRatio = trackingError > 0 ? (portAnn - spyAnn) / trackingError : null

    return { beta, alpha, rSquared, corr, trackingError, infoRatio }
  }, [benchData, equitySeries])

  // Report benchmark metrics for capture
  useEffect(() => {
    if (onMetricsComputed && riskMetrics) {
      onMetricsComputed({
        beta: riskMetrics.beta,
        alpha: riskMetrics.alpha * 100,
        r_squared: riskMetrics.rSquared,
        correlation: riskMetrics.corr,
        tracking_error: riskMetrics.trackingError * 100,
        info_ratio: riskMetrics.infoRatio,
      })
    }
  }, [riskMetrics, onMetricsComputed])

  if (equitySeries.length < 2) return null
  if (benchLoading) return <div className="font-mono text-xs text-inkMuted animate-pulse">Loading benchmarks…</div>
  if (!benchData || chartData.length === 0) return null

  return (
    <>
      <div>
        <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Benchmark Comparison</h2>
        <div className="font-mono text-[9px] text-inkDim mb-2">Portfolio vs major indices</div>
        <div className="flex flex-wrap gap-2 mb-3">
          <StatBadge label="Portfolio" value={<PrivateValue>{fmtPct(portfolioReturn)}</PrivateValue>} colorClass={clr(portfolioReturn)} />
          <StatBadge label="SPY" value={fmtPct(spyReturn)} colorClass={clr(spyReturn)} />
          <StatBadge label="QQQ" value={fmtPct(qqqReturn)} colorClass={clr(qqqReturn)} />
          <StatBadge label="α vs SPY" value={<PrivateValue>{fmtPct(alphaSpy)}</PrivateValue>} colorClass={clr(alphaSpy)} />
          <StatBadge label="α vs QQQ" value={<PrivateValue>{fmtPct(alphaQqq)}</PrivateValue>} colorClass={clr(alphaQqq)} />
        </div>
        <div className="card">
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }} axisLine={{ stroke: 'rgb(var(--color-border))' }} tickLine={false} minTickGap={60} />
              <YAxis tick={{ fontSize: 10, fill: 'rgb(var(--color-ink-muted))', fontFamily: '"DM Sans"' }} axisLine={false} tickLine={false} tickFormatter={v => v.toFixed(1) + '%'} width={45} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontFamily: '"DM Sans"', fontSize: 11 }} />
              <Line type="monotone" dataKey="portfolio" name="Portfolio" stroke="rgb(var(--color-forest))" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="spy" name="SPY" stroke="#5b7ea1" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
              <Line type="monotone" dataKey="qqq" name="QQQ" stroke="#c4953a" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Risk Metrics vs Benchmark */}
      {riskMetrics && (
        <div>
          <h2 className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Risk Metrics vs Benchmark</h2>
          <div className="font-mono text-[9px] text-inkDim mb-3">Relative to S&P 500 (SPY)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('beta', riskMetrics.beta, 'SPY') : undefined}>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Beta<InfoTooltip text="Sensitivity to market movements. Beta of 1.0 means the portfolio moves in lockstep with the S&P 500. Above 1.0 means more volatile than the market, below 1.0 means less volatile. Investors use this to understand market risk exposure." /></div>
              <div className="font-mono text-xl font-semibold text-ink">
                <PrivateValue>{riskMetrics.beta.toFixed(2)}</PrivateValue>
              </div>
              <div className="font-mono text-[10px] text-inkMuted mt-1">Market sensitivity</div>
            </div>
            <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('alpha', riskMetrics.alpha * 100, 'SPY') : undefined}>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Alpha<InfoTooltip text="Excess return not explained by market exposure (Jensen's Alpha). Positive alpha means the portfolio outperformed what its beta would predict. This is the core measure of manager skill — it's what investors are paying for." /></div>
              <div className={`font-mono text-xl font-semibold ${clr(riskMetrics.alpha)}`}>
                <PrivateValue>{fmtPct(riskMetrics.alpha * 100)}</PrivateValue>
              </div>
              <div className="font-mono text-[10px] text-inkMuted mt-1">Annualized excess return</div>
            </div>
            <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('r_squared', riskMetrics.rSquared, 'SPY') : undefined}>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">R-Squared<InfoTooltip text="How much of the portfolio's movement is explained by the benchmark (0 to 1). High R² means the portfolio closely tracks the market. Low R² means returns are driven by stock-specific factors rather than broad market moves." /></div>
              <div className="font-mono text-xl font-semibold text-ink">
                <PrivateValue>{riskMetrics.rSquared.toFixed(2)}</PrivateValue>
              </div>
              <div className="font-mono text-[10px] text-inkMuted mt-1">Benchmark explains {Math.round(riskMetrics.rSquared * 100)}% of variance</div>
            </div>
            <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('correlation', riskMetrics.corr, 'SPY') : undefined}>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Correlation<InfoTooltip text="Direction and strength of the relationship between portfolio and benchmark returns (-1 to +1). High positive correlation means the portfolio moves with the market. Low correlation suggests diversification benefits." /></div>
              <div className="font-mono text-xl font-semibold text-ink">
                <PrivateValue>{(riskMetrics.corr >= 0 ? '+' : '') + riskMetrics.corr.toFixed(2)}</PrivateValue>
              </div>
              <div className="font-mono text-[10px] text-inkMuted mt-1">vs S&P 500</div>
            </div>
            <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('tracking_error', riskMetrics.trackingError * 100, 'SPY') : undefined}>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Tracking Error<InfoTooltip text="Annualized standard deviation of the difference between portfolio and benchmark returns. Measures how consistently the portfolio deviates from the index. Active managers typically have 3-8% tracking error." /></div>
              <div className="font-mono text-xl font-semibold text-ink">
                <PrivateValue>{(riskMetrics.trackingError * 100).toFixed(2)}%</PrivateValue>
              </div>
              <div className="font-mono text-[10px] text-inkMuted mt-1">Annualized deviation from benchmark</div>
            </div>
            <div className="card metric-card-clickable" onClick={onMetricClick ? () => onMetricClick('info_ratio', riskMetrics.infoRatio, 'SPY') : undefined}>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Info Ratio<InfoTooltip text="Excess return over the benchmark per unit of tracking error. Measures how efficiently the portfolio generates alpha relative to its deviation from the index. Above 0.5 is good, above 1.0 is exceptional." /></div>
              <div className={`font-mono text-xl font-semibold ${riskMetrics.infoRatio != null ? clr(riskMetrics.infoRatio) : 'text-inkMuted'}`}>
                <PrivateValue>{riskMetrics.infoRatio != null ? (riskMetrics.infoRatio >= 0 ? '+' : '') + riskMetrics.infoRatio.toFixed(2) : 'N/A'}</PrivateValue>
              </div>
              <div className="font-mono text-[10px] text-inkMuted mt-1">Excess return per unit risk</div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Analytics() {
  const { user } = useAuth()
  const { privacyMode } = usePrivacy()

  const [trades, setTrades] = useState([])
  const [openTrades, setOpenTrades] = useState([])
  const [positions, setPositions] = useState([])
  const [accounts, setAccounts] = useState([])
  const [nicknameMap, setNicknameMap] = useState({})
  const [snapshots, setSnapshots] = useState([])
  const [snapshotError, setSnapshotError] = useState(null)
  const [loading, setLoading] = useState(true)

  // View toggle
  const [analyticsView, setAnalyticsView] = useState('portfolio')

  // Filters
  const [datePreset, setDatePreset] = useState('thisYear')
  const [dateStart, setDateStart] = useState('')
  const [dateEnd, setDateEnd] = useState('')
  const [selectedAccounts, setSelectedAccounts] = useState([])

  // Metric trend slide-over state
  const [trendMetric, setTrendMetric] = useState(null) // { metricName, metricView, benchmark, currentValue }

  // Metrics capture — wait for all sources then flush (upsert is idempotent)
  const EXPECTED_SOURCES = {
    portfolio: ['portfolioView', 'portfolioBenchmark'],
    trading: ['performanceKPIs', 'riskMetrics'],
  }
  const pendingMetricsRef = useRef({ portfolio: {}, trading: {} })
  const reportedSourcesRef = useRef({ portfolio: new Set(), trading: new Set() })
  const fallbackTimerRef = useRef({ portfolio: null, trading: null })

  const reportMetrics = useCallback((view, source, metricsPartial) => {
    if (!user || view === 'portfolio') return
    // Portfolio history is not eligible for metric capture until cashflows and coverage are certified.
    // Merge metrics from this source
    pendingMetricsRef.current[view] = { ...pendingMetricsRef.current[view], ...metricsPartial }
    reportedSourcesRef.current[view].add(source)

    // Check if all expected sources have reported
    const allIn = EXPECTED_SOURCES[view].every(s => reportedSourcesRef.current[view].has(s))

    if (allIn) {
      // All sources reported — flush immediately
      clearTimeout(fallbackTimerRef.current[view])
      fallbackTimerRef.current[view] = null
      captureMetricsToHistory(user, view, pendingMetricsRef.current[view])
    } else if (!fallbackTimerRef.current[view]) {
      // First source reported — start 3s fallback in case others never arrive
      fallbackTimerRef.current[view] = setTimeout(() => {
        fallbackTimerRef.current[view] = null
        captureMetricsToHistory(user, view, pendingMetricsRef.current[view])
      }, 3000)
    }
  }, [user])

  // One callback per metric source — each tagged so we know when all have reported
  const handlePortfolioViewMetrics = useCallback((m) => reportMetrics('portfolio', 'portfolioView', m), [reportMetrics])
  const handleBenchmarkMetrics = useCallback((m) => reportMetrics('portfolio', 'portfolioBenchmark', m), [reportMetrics])
  const handlePerfKPIMetrics = useCallback((m) => reportMetrics('trading', 'performanceKPIs', m), [reportMetrics])
  const handleRiskMetricsCapture = useCallback((m) => reportMetrics('trading', 'riskMetrics', m), [reportMetrics])

  // Metric click handler — opens the trend slide-over
  const handlePortfolioMetricClick = useCallback((metricName, currentValue, benchmark = null) => {
    setTrendMetric({ metricName, metricView: 'portfolio', benchmark, currentValue })
  }, [])

  const handleTradingMetricClick = useCallback((metricName, currentValue, benchmark = null) => {
    setTrendMetric({ metricName, metricView: 'trading', benchmark, currentValue })
  }, [])

  // Load data
  useEffect(() => {
    if (!user) return
    async function load() {
      if (trades.length === 0) setLoading(true)
      const [
        { data: allTrades },
        { data: openPartial },
        { data: pos },
        { data: accts },
        { data: nicks },
        { data: snaps, error: snapsError },
      ] = await Promise.all([
        supabase.from('trades').select('*').eq('user_id', user.id),
        supabase.from('trades').select('*').eq('user_id', user.id).in('status', ['open', 'partial']).not('stop_loss', 'is', null),
        supabase.from('schwab_positions').select('*').eq('user_id', user.id).eq('status', 'open'),
        supabase.from('schwab_accounts').select('*').eq('user_id', user.id),
        supabase.from('account_nicknames').select('account_number, nickname').eq('user_id', user.id),
        loadBalanceSnapshots(supabase, user.id),
      ])
      setTrades(allTrades || [])
      setOpenTrades(openPartial || [])
      setPositions(pos || [])
      setAccounts(accts || [])
      setSnapshots(snaps || [])
      setSnapshotError(snapsError)
      setNicknameMap(Object.fromEntries((nicks || []).map(r => [r.account_number, r.nickname])))
      setLoading(false)
    }
    load()
  }, [user])

  // Unique accounts from trades
  const accountList = useMemo(() => {
    const set = new Set(trades.map(t => t.account_number))
    return [...set].sort()
  }, [trades])

  // Effective date range
  const [effStart, effEnd] = useMemo(() => {
    if (datePreset === 'custom') return [dateStart, dateEnd]
    if (datePreset === 'all') return ['', '']
    return getDateRange(datePreset)
  }, [datePreset, dateStart, dateEnd])

  // Filter trades for sections 1-3
  const filteredTrades = useMemo(() => {
    return trades.filter(t => {
      if (effStart || effEnd) {
        const exit = t.exit_date || ''
        const entry = t.entry_date || ''
        const exitInRange = exit && (!effStart || exit >= effStart) && (!effEnd || exit <= effEnd)
        const entryInRange = (!effStart || entry >= effStart) && (!effEnd || entry <= effEnd)
        if (!exitInRange && !entryInRange) return false
      }
      if (selectedAccounts.length > 0 && !selectedAccounts.includes(t.account_number)) return false
      return true
    })
  }, [trades, effStart, effEnd, selectedAccounts])

  // Total portfolio equity from schwab_accounts (equity is JSONB: {equity: number})
  const totalEquity = useMemo(() => {
    return accounts.reduce((s, a) => s + Number(a.total_value || a.equity?.equity || 0), 0)
  }, [accounts])

  function toggleAccount(acct) {
    setSelectedAccounts(prev =>
      prev.includes(acct) ? prev.filter(a => a !== acct) : [...prev, acct]
    )
  }

  function acctLabel(num) {
    return nicknameMap[num] || ('···' + num.slice(-4))
  }


  return (
    <div className="space-y-8">
      {/* Header + View Toggle */}
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Performance</div>
          <h1 className="font-black text-2xl uppercase tracking-wide text-ink">Analytics</h1>
        </div>
        <div className="flex rounded-full border border-border overflow-hidden">
          {['portfolio', 'trading'].map(v => (
            <button
              key={v}
              onClick={() => setAnalyticsView(v)}
              className={`font-mono text-[10px] px-4 py-1.5 transition-colors capitalize ${
                analyticsView === v
                  ? 'bg-ink text-parchment'
                  : 'bg-canvas2 text-inkMuted hover:text-ink'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-canvas border border-border rounded-xl px-4 py-3 space-y-3">
        {/* Date range presets */}
        <div className="flex flex-wrap items-center gap-1.5">
          {DATE_PRESETS.map(p => (
            <Pill key={p.key} label={p.label} active={datePreset === p.key} onClick={() => setDatePreset(p.key)} />
          ))}
          {datePreset === 'custom' && (
            <div className="flex items-center gap-1.5 ml-2">
              <input type="date" value={dateStart} onChange={e => { setDateStart(e.target.value); setDatePreset('custom') }}
                className="font-mono text-[11px] px-2 py-1 rounded-lg bg-canvas2 border border-border text-ink w-[130px]" />
              <span className="text-inkMuted text-xs">—</span>
              <input type="date" value={dateEnd} onChange={e => { setDateEnd(e.target.value); setDatePreset('custom') }}
                className="font-mono text-[11px] px-2 py-1 rounded-lg bg-canvas2 border border-border text-ink w-[130px]" />
            </div>
          )}
        </div>

        {/* Account pills */}
        {accountList.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-[10px] text-inkMuted uppercase tracking-widest mr-1">Accounts:</span>
            {accountList.map(a => (
              <Pill key={a} label={acctLabel(a)} active={selectedAccounts.includes(a)} onClick={() => toggleAccount(a)} />
            ))}
            {selectedAccounts.length > 0 && (
              <button onClick={() => setSelectedAccounts([])} className="font-mono text-[10px] text-inkMuted hover:text-ink underline ml-1">
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {analyticsView === 'portfolio' ? (
        <BalanceHistory
          showPerformance
          loading={loading}
          error={snapshotError}
          snapshots={snapshots}
          selectedAccounts={selectedAccounts}
          effStart={effStart}
          effEnd={effEnd}
          nicknameMap={nicknameMap}
        />
      ) : (
        <>
          {/* Section 1 — Performance KPIs */}
          <PerformanceKPIs trades={filteredTrades} totalEquity={totalEquity} onMetricClick={handleTradingMetricClick} onMetricsComputed={handlePerfKPIMetrics} />

          {/* Section 2 — Equity Curve & Drawdown */}
          <EquityCurveSection trades={filteredTrades} />

          {/* Section 3 — Risk-Adjusted Metrics */}
          <RiskMetrics trades={filteredTrades} onMetricClick={handleTradingMetricClick} onMetricsComputed={handleRiskMetricsCapture} />

          {/* Section 4 — Portfolio at Risk */}
          <PortfolioAtRisk
            openTrades={openTrades}
            positions={positions}
            accounts={accounts}
            nicknameMap={nicknameMap}
          />

          {/* Section 5 — Benchmark Comparison */}
          <BenchmarkComparison trades={filteredTrades} effStart={effStart} effEnd={effEnd} totalEquity={totalEquity} />

          {/* Section 6 — Trade Analytics */}
          <TradeAnalyticsSection trades={filteredTrades} />

          {/* Section 7 — Segmented Performance */}
          <SegmentedPerformance trades={filteredTrades} />

          {/* Section 8 — Calendar Heatmap */}
          <CalendarHeatmap trades={filteredTrades} />
        </>
      )}

      {/* Metric Trend Slide-Over */}
      {trendMetric && (
        <MetricTrendSlideOver
          metricName={trendMetric.metricName}
          metricView={trendMetric.metricView}
          benchmark={trendMetric.benchmark}
          currentValue={trendMetric.currentValue}
          onClose={() => setTrendMetric(null)}
        />
      )}
    </div>
  )
}
